import React, { useId, useState } from 'react'
import { Link } from '@inertiajs/react'
import { AlertTriangle, ChevronDown, LineChart, RotateCcw } from 'lucide-react'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import HotelChip from '@/components/groups/HotelChip'
import ReviewRows from '@/components/review/ReviewRows'
import { cn } from '@/lib/utils'
import PersonLabel, { PersonAvatar } from '@/components/history/PersonLabel'
import type { HistoryEntry } from '@/types/history'

/**
 * ONE ENTRY OF A HOTEL'S HISTORY (docs/plans/2026-10-20-change-safety.md §5.17.2): who, when, and one
 * strong sentence, readable at a glance; what changed, value by value, one click away.
 *
 * Collapsed, the row answers "who changed what, when": the hotel's local time in the left column,
 * the person (by email), the area and the source, and the server's summary. An entry that was undone,
 * or that undid another, says so under the summary, where it cannot be missed.
 *
 * Expanded, it shows the changed values (the review's own rows, read-only, headed "Before"), the
 * entry's warnings, the link to the price log from that moment ("Price changes since then", never a
 * claim that this change moved a price, R2.8) and Undo when the server offers it (§5.11.1). When
 * the server rules Undo out for a reason the person can act on (`unavailable`), its sentence stands
 * where the button would be. Every word and figure is the server's.
 *
 * The whole header toggles the detail for a pointer; the chevron is the button a keyboard reaches,
 * so the row needs no interactive element nested inside another.
 */
interface ChangeEntryProps {
  entry: HistoryEntry
  /** Undo was asked for on this entry. The page owns the dialog (`useUndo`). */
  onUndo: (entry: HistoryEntry) => void
}

const BADGE = 'shrink-0 border-0 px-2 py-0 text-[11px] font-semibold'

export default function ChangeEntry({ entry, onUndo }: ChangeEntryProps) {
  const [open, setOpen] = useState(false)
  const detailId = useId()
  const undone = entry.undo.state === 'undone' ? entry.undo : null
  const unavailable = entry.undo.state === 'unavailable' ? entry.undo : null

  return (
    <li data-entry={entry.id}>
      <div
        className="flex cursor-pointer items-start gap-3 px-5 py-4 transition-colors hover:bg-muted/30"
        onClick={() => {
          // Selecting an email or a sentence to copy it is not a request to open the entry.
          if (window.getSelection()?.toString()) return
          setOpen((value) => !value)
        }}
      >
        <span className="hidden w-10 shrink-0 pt-2 text-xs font-medium tabular-nums text-muted-foreground sm:block">
          <time dateTime={entry.at}>{entry.time}</time>
        </span>
        <PersonAvatar person={entry.person} />

        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
            <PersonLabel person={entry.person} className="max-w-full" />
            <span className="text-xs tabular-nums text-muted-foreground sm:hidden">
              <time dateTime={entry.at}>{entry.time}</time>
            </span>
            <Badge className={cn(BADGE, 'bg-slate-100 text-slate-700 hover:bg-slate-100')}>{entry.areaLabel}</Badge>
            {entry.sourceLabel && (
              <Badge variant="outline" className={cn(BADGE, 'border border-border bg-white text-slate-600')}>
                {entry.sourceLabel}
              </Badge>
            )}
            {/* Uncapped: which listing an entry was made on is the point of the chip. */}
            <HotelChip name={entry.hotelName} size="sm" className="max-w-full" />
          </div>

          <p className="mt-1 text-sm font-medium text-foreground [overflow-wrap:anywhere]">{entry.summary}</p>

          {entry.undoOf && (
            <p className="mt-1 flex items-center gap-1.5 text-xs text-muted-foreground">
              <RotateCcw className="h-3 w-3 shrink-0" aria-hidden="true" />
              <span>{entry.undoOf.label}</span>
            </p>
          )}
          {undone && (
            <p className="mt-1 flex items-center gap-1.5 text-xs text-muted-foreground" data-undo-state="undone">
              <RotateCcw className="h-3 w-3 shrink-0" aria-hidden="true" />
              <span>{undone.label}</span>
            </p>
          )}
        </div>

        <Button
          variant="ghost"
          size="sm"
          className="h-8 w-8 shrink-0 rounded-lg p-0"
          data-entry-toggle=""
          aria-expanded={open}
          aria-controls={detailId}
          aria-label={open ? 'Hide what changed' : 'Show what changed'}
          onClick={(event) => {
            event.stopPropagation()
            setOpen((value) => !value)
          }}
        >
          <ChevronDown className={cn('h-4 w-4 transition-transform duration-150', open && 'rotate-180')} />
        </Button>
      </div>

      {open && (
        <div id={detailId} className="space-y-3 px-5 pb-4 sm:pl-[7.25rem]" data-testid="entry-detail">
          {/* The review's own rows, read-only: one implementation wherever a change is shown (EP-36). */}
          {entry.rows.length > 0 && <ReviewRows rows={entry.rows} variant="record" moreRows={entry.moreRows} />}

          {entry.warnings.length > 0 && (
            <ul className="space-y-1 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2">
              {entry.warnings.map((line, i) => (
                <li key={i} className="flex items-start gap-1.5 text-xs font-medium text-amber-700">
                  <AlertTriangle className="mt-px h-3.5 w-3.5 shrink-0 text-amber-500" aria-hidden="true" />
                  <span>{line}</span>
                </li>
              ))}
            </ul>
          )}

          {(entry.pricesLink || entry.undo.state === 'available' || unavailable) && (
            <div className="flex flex-wrap items-center justify-between gap-2">
              {entry.pricesLink ? (
                <Link
                  href={entry.pricesLink.href}
                  className="inline-flex cursor-pointer items-center gap-1.5 rounded-lg text-xs font-medium text-primary hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/40"
                >
                  <LineChart className="h-3.5 w-3.5" aria-hidden="true" />
                  {entry.pricesLink.label}
                </Link>
              ) : (
                <span />
              )}
              {entry.undo.state === 'available' && (
                <Button
                  variant="ghost"
                  size="sm"
                  className="h-8 rounded-lg"
                  data-undo-button=""
                  onClick={(event) => {
                    // The review dialog returns focus to whatever opened it. A pointer press does not
                    // focus a button in every browser, so the button takes focus before the dialog
                    // opens and gets it back when the dialog closes.
                    event.currentTarget.focus()
                    onUndo(entry)
                  }}
                >
                  <RotateCcw className="mr-1.5 h-3.5 w-3.5" />
                  {entry.undo.label}
                </Button>
              )}
              {unavailable && (
                <p className="max-w-md text-xs text-muted-foreground sm:text-right" data-undo-state="unavailable">
                  {unavailable.label}
                </p>
              )}
            </div>
          )}
        </div>
      )}
    </li>
  )
}
